import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, Clock, ChefHat, PackageCheck, RefreshCw, Volume2, VolumeX, Power, BarChart3, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { money } from "../lib/menu";

const COLUMNS = [
  { key: "NEW", label: "New Orders", icon: Clock, next: "PREPARING", nextLabel: "Start Cooking" },
  { key: "PREPARING", label: "Preparing", icon: ChefHat, next: "READY", nextLabel: "Mark Ready" },
  { key: "READY", label: "Ready", icon: PackageCheck, next: "COMPLETED", nextLabel: "Picked Up" },
];

const elapsed = (iso) => {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  return mins <= 0 ? "just now" : `${mins} min ago`;
};

export default function Kds() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [sound, setSound] = useState(true);
  const [orderingEnabled, setOrderingEnabled] = useState(true);
  const [tick, setTick] = useState(0);
  const prevNewIds = useRef(new Set());
  const audioRef = useRef(null);

  const beep = useCallback(() => {
    if (!sound) return;
    try {
      const ctx = audioRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioRef.current = ctx;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = 880; o.type = "square";
      g.gain.setValueAtTime(0.15, ctx.currentTime);
      o.start();
      o.stop(ctx.currentTime + 0.25);
    } catch (_) { /* audio not supported */ }
  }, [sound]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/kds/orders");
      const newIds = new Set(data.filter((o) => o.status === "NEW").map((o) => o.id));
      let hasNew = false;
      newIds.forEach((id) => { if (!prevNewIds.current.has(id)) hasNew = true; });
      if (hasNew && prevNewIds.current.size >= 0) beep();
      prevNewIds.current = newIds;
      setOrders(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("kds_token");
        navigate("/kds/login");
      }
    }
  }, [beep, navigate]);

  useEffect(() => {
    if (!localStorage.getItem("kds_token")) { navigate("/kds/login"); return; }
    load();
    api.get("/store/status").then(({ data }) => setOrderingEnabled(data.ordering_enabled)).catch(() => {});
    const poll = setInterval(load, 5000);
    const clock = setInterval(() => setTick((t) => t + 1), 30000);
    return () => { clearInterval(poll); clearInterval(clock); };
  }, [load, navigate]);

  const toggleOrdering = async () => {
    const next = !orderingEnabled;
    setOrderingEnabled(next);
    try {
      await api.patch("/store/status", { ordering_enabled: next });
      toast.success(next ? "Online ordering is now ON" : "Online ordering is now OFF");
    } catch (err) {
      setOrderingEnabled(!next);
      toast.error("Could not update ordering status");
    }
  };

  const advance = async (order, status) => {
    // optimistic
    setOrders((prev) => status === "COMPLETED" ? prev.filter((o) => o.id !== order.id) : prev.map((o) => o.id === order.id ? { ...o, status } : o));
    try {
      await api.patch(`/kds/orders/${order.id}/status`, { status });
      load();
    } catch (_) { load(); }
  };

  const logout = () => { localStorage.removeItem("kds_token"); navigate("/kds/login"); };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <header className="border-b border-neon-green/20 px-5 sm:px-8 h-[64px] flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <span className="font-heading text-3xl text-neon-green neon-glow-green">67</span>
          <span className="font-heading text-xl text-neon-yellow tracking-wide">KITCHEN DISPLAY</span>
          <span className="hidden sm:inline font-body text-xs text-neutral-500 ml-3">{orders.length} active {orders.length === 1 ? "order" : "orders"}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/pos")}
            data-testid="kds-pos-link"
            title="67POS — cash / in-store"
            className="inline-flex items-center gap-2 font-heading text-base sm:text-lg px-3 sm:px-4 py-1.5 border border-neon-green/60 text-neon-green hover:bg-neon-green hover:text-black transition-colors"
          >
            <DollarSign size={16} /> <span className="hidden sm:inline">67POS</span>
          </button>
          <button
            onClick={() => navigate("/dashboard")}
            data-testid="kds-dashboard-link"
            title="Owner dashboard & bookkeeping"
            className="inline-flex items-center gap-2 font-heading text-base sm:text-lg px-3 sm:px-4 py-1.5 border border-neon-yellow/50 text-neon-yellow hover:bg-neon-yellow hover:text-black transition-colors"
          >
            <BarChart3 size={16} /> <span className="hidden sm:inline">Dashboard</span>
          </button>
          <button
            onClick={toggleOrdering}
            data-testid="kds-ordering-toggle"
            title="Turn online ordering on/off"
            className={`inline-flex items-center gap-2 font-heading text-base sm:text-lg px-3 sm:px-4 py-1.5 border transition-colors ${
              orderingEnabled
                ? "border-neon-green bg-neon-green text-black hover:bg-neon-yellow hover:border-neon-yellow"
                : "border-red-500 text-red-500 hover:bg-red-500 hover:text-black"
            }`}
          >
            <Power size={16} /> Ordering: {orderingEnabled ? "ON" : "OFF"}
          </button>
          <button onClick={() => setSound((s) => !s)} data-testid="kds-sound-toggle" title="Toggle sound" className="p-2 text-neon-green hover:bg-neon-green/10">{sound ? <Volume2 size={20} /> : <VolumeX size={20} />}</button>
          <button onClick={load} data-testid="kds-refresh" title="Refresh" className="p-2 text-neon-green hover:bg-neon-green/10"><RefreshCw size={20} /></button>
          <button onClick={logout} data-testid="kds-logout" className="inline-flex items-center gap-2 font-heading text-lg border border-neon-green/40 text-neon-green px-4 py-1.5 hover:bg-neon-green hover:text-black transition-colors"><LogOut size={16} /> <span className="hidden sm:inline">Logout</span></button>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-px bg-neon-green/10 overflow-hidden">
        {COLUMNS.map((col) => {
          const colOrders = orders.filter((o) => o.status === col.key);
          const Icon = col.icon;
          return (
            <div key={col.key} className="bg-black flex flex-col overflow-hidden" data-testid={`kds-col-${col.key.toLowerCase()}`}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-neon-green/20 sticky top-0 bg-black">
                <h2 className="font-heading text-2xl uppercase tracking-wide text-neon-green flex items-center gap-2"><Icon size={20} /> {col.label}</h2>
                <span className="font-heading text-2xl text-neon-yellow">{colOrders.length}</span>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                <AnimatePresence>
                  {colOrders.map((o) => (
                    <motion.div
                      key={o.id}
                      layout
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      data-testid={`kds-order-${o.order_number}`}
                      className={`border p-4 bg-elevated ${col.key === "NEW" ? "border-neon-yellow/60" : "border-neon-green/25"}`}
                    >
                      <div className="flex items-baseline justify-between">
                        <span className="font-heading text-3xl text-neon-yellow">#{o.order_number}</span>
                        <span className="font-body text-xs text-neutral-500">{elapsed(o.created_at)}</span>
                      </div>
                      <p className="font-body text-sm text-neutral-300 mt-0.5">{o.customer_name} · {o.phone}</p>
                      <p className="font-body text-xs text-neon-green mt-0.5">Pickup: {o.pickup_time || "ASAP"}</p>
                      <div className="mt-3 space-y-2 border-t border-neon-green/10 pt-3">
                        {o.items.map((it, i) => (
                          <div key={i}>
                            <p className="font-heading text-lg text-white uppercase tracking-wide">{it.quantity}× {it.name}</p>
                            {it.options?.length > 0 && (
                              <ul className="ml-3">
                                {it.options.map((op, j) => <li key={j} className="font-body text-xs text-neutral-400">· {op}</li>)}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => advance(o, col.next)}
                        data-testid={`kds-advance-${o.order_number}`}
                        className="mt-4 w-full font-heading text-lg tracking-wide bg-neon-green text-black py-2 hover:bg-neon-yellow transition-colors"
                      >
                        {col.nextLabel}
                      </button>
                    </motion.div>
                  ))}
                </AnimatePresence>
                {colOrders.length === 0 && <p className="font-body text-sm text-neutral-700 text-center py-10">No orders</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
